import { addDays, format, startOfWeek } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { OperatingHoursResponseDto } from '@pro-sto/api-client';
import { ROUTES } from '@/constants/routes';
import {
  buildTimeSlots,
  formatMinutesToTime,
  getWorkingHoursRangeForDate,
} from '@/utils/scheduleHelpers';
import { hasShiftRoster } from '@/stores/shiftDraftModel';
import type { PlannedTimeBounds } from '@/stores/shiftDraftModel';
import type { ShiftDay, ShiftStatus } from '@/types/shift.types';

export const EMPLOYEES_TABS = ['shifts', 'employees', 'payroll'] as const;
export type EmployeesTab = (typeof EMPLOYEES_TABS)[number];
export type ShiftsView = 'week' | 'day';

const WEEKDAY_LABELS = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'] as const;

const STATUS_LABEL: Record<ShiftStatus, string> = {
  created: 'Создана',
  open: 'Открыта',
  closed: 'Закрыта',
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface WeekRange {
  start: Date;
  end: Date;
  from: string;
  to: string;
}

export interface DisplayedWeekState {
  isLoading: boolean;
  loadError: string | null;
  loadedFrom: string | null;
  loadedTo: string | null;
}

export const parseEmployeesTab = (value: string | null): EmployeesTab => {
  if (value === 'shifts' || value === 'employees' || value === 'payroll') {
    return value;
  }
  return 'employees';
};

export const parseShiftsView = (value: string | null): ShiftsView =>
  value === 'day' ? 'day' : 'week';

export interface ShiftsDayLocationState {
  stayOnDayDate?: string;
}

export const employeesShiftsPath = (view: ShiftsView, date: string): string => {
  const params = new URLSearchParams({ tab: 'shifts', view, date });
  return `${ROUTES.EMPLOYEES}?${params.toString()}`;
};

export const toDateParam = (date: Date): string => format(date, 'yyyy-MM-dd');

export const parseDateParam = (value: string | null, today = new Date()): Date => {
  const match = value ? ISO_DATE.exec(value) : null;
  if (!match) {
    return startOfLocalDay(today);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return startOfLocalDay(today);
  }
  return date;
};

export const weekBounds = (date: Date): WeekRange => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = addDays(start, 6);
  return {
    start,
    end,
    from: toDateParam(start),
    to: toDateParam(end),
  };
};

export const weekDays = (start: Date): Date[] =>
  Array.from({ length: 7 }, (_, index) => addDays(start, index));

/**
 * Пока новый интервал не пришёл, на экране остаётся прошлая неделя.
 * Ошибка и первая загрузка показывают запрошенную.
 */
export const displayedWeek = (selectedDate: Date, state: DisplayedWeekState): WeekRange => {
  const requested = weekBounds(selectedDate);
  const hasLoadedWeek = state.loadedFrom !== null && state.loadedTo !== null;
  const rangeChanged =
    state.loadedFrom !== requested.from || state.loadedTo !== requested.to;

  if (state.isLoading && !state.loadError && hasLoadedWeek && rangeChanged && state.loadedFrom) {
    return weekBounds(parseDateParam(state.loadedFrom, selectedDate));
  }

  return requested;
};

export const formatDayHeading = (date: Date): string => {
  const label = format(date, 'EEEE, d MMMM', { locale: ru });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const formatWeekTitle = (start: Date, end: Date): string => {
  const sameMonth =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${format(start, 'd', { locale: ru })}–${format(end, 'd MMMM', { locale: ru })}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'd MMMM', { locale: ru })} – ${format(end, 'd MMMM', { locale: ru })}`;
  }

  return `${format(start, 'd MMMM yyyy', { locale: ru })} – ${format(end, 'd MMMM yyyy', { locale: ru })}`;
};

export const weekdayLabel = (date: Date): string => WEEKDAY_LABELS[date.getDay()];

export const dayTitle = (date: Date): string => format(date, 'd MMMM', { locale: ru });

export const shiftDateKey = (value: string): string => value.slice(0, 10);

export const findShiftForDate = (shifts: ShiftDay[], date: Date): ShiftDay | undefined => {
  const key = toDateParam(date);
  return shifts.find((shift) => shiftDateKey(shift.date) === key);
};

export const shiftStatusLabel = (shift: ShiftDay | undefined): string => {
  if (!shift || !hasShiftRoster(shift)) return 'Не создана';
  return STATUS_LABEL[shift.status];
};

export const showWeekCreateShift = (shift: ShiftDay | undefined): boolean =>
  !shift || !hasShiftRoster(shift);

export const formatPlannedTime = (start: string | null, end: string | null): string => {
  const from = clockLabel(start);
  const to = clockLabel(end);
  if (from && to) return `${from}-${to}`;
  return from ?? to ?? '';
};

/**
 * До закрытия и если строки нет — часы центра на дату.
 * Выходной (и смена не закрыта) — пустой интервал.
 * Закрытая смена — фактические opened_at–closed_at.
 */
export const shiftIntervalLabel = (
  shift: ShiftDay | undefined,
  date: Date,
  regularSchedule: OperatingHoursResponseDto[],
  specialDates: OperatingHoursResponseDto[],
  timeZone: string,
): string => {
  if (shift?.status === 'closed') {
    const start = formatIsoClock(shift.opened_at, timeZone);
    const end = formatIsoClock(shift.closed_at, timeZone);
    if (start && end) return `${start}–${end}`;
    return start ?? end ?? '';
  }

  const range = getWorkingHoursRangeForDate(date, regularSchedule, specialDates);
  if (range.isClosed) return '';

  return `${formatMinutesToTime(range.openMinutes)}–${formatMinutesToTime(range.closeMinutes)}`;
};

export interface PlannedTimeWindow {
  bounds: PlannedTimeBounds;
  slots: string[];
}

/**
 * Слоты планового времени — часы центра на эту дату, без запаса.
 * В API отдельного окна «± час» нет: header.start/end — локальные часы центра,
 * в выходной null. Выходной — ограничения нет, в пикере остаётся полный день.
 */
export const plannedTimeWindow = (
  date: Date,
  regularSchedule: OperatingHoursResponseDto[],
  specialDates: OperatingHoursResponseDto[],
): PlannedTimeWindow | null => {
  const range = getWorkingHoursRangeForDate(date, regularSchedule, specialDates);
  if (range.isClosed) return null;

  const fromMinutes = range.openMinutes;
  const toMinutes = range.closeMinutes;
  const from = formatMinutesToTime(fromMinutes);
  const to = formatMinutesToTime(toMinutes);

  return {
    bounds: {
      fromMinutes,
      toMinutes,
      label: `${from}–${to}`,
    },
    slots: buildTimeSlots(fromMinutes, toMinutes),
  };
};

const startOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const clockLabel = (value: string | null): string | null => {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})/.exec(value);
  return match ? `${match[1]}:${match[2]}` : null;
};

export const formatIsoClock = (value: string | null, timeZone: string): string | null => {
  if (!value) return null;

  const plainClock = clockLabel(value);
  if (plainClock && !value.includes('T')) return plainClock;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return plainClock;

  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: timeZone || 'Europe/Moscow',
  }).formatToParts(date);
  const hour = parts.find((part) => part.type === 'hour')?.value;
  const minute = parts.find((part) => part.type === 'minute')?.value;
  if (!hour || !minute) return null;
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
};
